/**
 * quoteAPI — API helpers for the Quote Builder.
 *
 * Wraps the main API client with quote-specific operations.
 * Used by useQuoteAutoSave and lifecycle state transitions.
 */

import api from '../services/api';

// The reader's row array; a malformed response rejects so the caller shows
// an error, never an empty state (09-08).
function ackList(res) {
  const list = Array.isArray(res) ? res : (res?.acknowledgments ?? res?.data?.acknowledgments);
  if (!Array.isArray(list)) throw new Error('Unexpected acknowledgments response');
  return list;
}

export const quotesAPI = {
  /**
   * Create a new quote.
   * @param {object} data - Quote data
   * @returns {Promise<{id: string, status: string, updated_at: string}>}
   */
  async createQuote(data) {
    const res = await api.createQuote(data);
    return res?.data || res;
  },

  /**
   * Update an existing quote (auto-save target).
   * @param {string} id - Quote ID
   * @param {object} data - Updated quote data
   * @returns {Promise<{id: string, updated_at: string}>}
   */
  async updateQuote(id, data) {
    const res = await api.updateQuote(id, data);
    return res?.data || res;
  },

  // Q-3 markSent/`?action=send` removed (IDI-118, 2026-09-03): requireESign
  // has been hardcoded true since W3.6 (2026-08-04), so this fallback was
  // dead code — no UI path ever called it. sendForSignature (below) is now
  // the only send path; it transitions status=sent directly.

  /**
   * Q-2b: Send-for-signature — real signature workflow. PT-53A W2.1 (DEF-108):
   * accepts a composer payload so recipients/subject/message are persisted.
   * @param {string} id
   * @param {object=} composer
   * @returns {Promise<{signature_token: string, status: string}>}
   */
  async sendForSignature(id, composer = null, displayOptions = null) {
    const res = await api.sendForSignature(id, composer, displayOptions);
    return res?.data || res;
  },

  /**
   * IDI-132: Reopen and Edit — the only way to undo a send or a signature
   * (replaces Mark Unsent). Server action: sent/won → active; clears the
   * signature, token, expiry and acceptedConfigurationId link; keeps the
   * snapshot; writes a "reopened" acknowledgment. 409 once converted.
   * @param {string} id - Quote ID
   * @returns {Promise<{id: string, status: string}>}
   */
  async reopen(id) {
    const res = await api.post('/v1/quotes?action=reopen', { quote_id: id });
    return res?.data || res;
  },

  // Q-4 Convert to Job: removed (DEF-141b, 2026-07-31).
  // The single canonical path is now:
  //   QuoteDetail.handleConvert → navigate('/record-decision')
  //   → RecordDecisionPage → api.convertQuoteToJob(id, selections)
  // Ruling 3e-ii: one conversion surface, /record-decision.

  /**
   * Mark quote as Lost.
   * @param {string} id - Quote ID
   * @param {string} reason - Loss reason
   * @returns {Promise<{id: string, status: string}>}
   */
  async markLost(id, reason) {
    const res = await api.markQuoteAsLost(id, reason);
    return res?.data || res;
  },

  /**
   * Mark quote as Expired.
   * @param {string} id - Quote ID
   * @returns {Promise<{id: string, status: string}>}
   */
  async markExpired(id) {
    const res = await api.markQuoteAsExpired(id);
    return res?.data || res;
  },

  /**
   * Get full quote data for editing.
   * @param {string} id - Quote ID
   * @returns {Promise<object>}
   */
  async getQuote(id) {
    const res = await api.getQuote(id);
    return res?.data || res;
  },

  /**
   * IDI-540: Read a quote's acknowledgment rows (IDI-539 reader).
   * Resolves to the row array (possibly empty); rejects when the request
   * fails so the caller shows an error, never an empty state (09-08).
   * @param {string} quoteId - Quote ID
   * @returns {Promise<Array>}
   */
  async getAcknowledgements(quoteId) {
    return ackList(await api.getQuoteAcknowledgements(quoteId));
  },

  /**
   * IDI-542: a change order's acknowledgment rows (same reader, CO filter).
   * @param {string} changeOrderId
   * @returns {Promise<Array>}
   */
  async getChangeOrderAcknowledgements(changeOrderId) {
    return ackList(await api.getChangeOrderAcknowledgements(changeOrderId));
  },

  /**
   * IDI-542: a job's acknowledgment rows — its quote's plus its change
   * orders', each carrying documentType / documentId / documentLabel.
   * @param {string} jobId
   * @returns {Promise<Array>}
   */
  async getJobAcknowledgements(jobId) {
    return ackList(await api.getJobAcknowledgements(jobId));
  },

  /**
   * Get customer data for prefill.
   * @param {string} customerId - Customer ID
   * @returns {Promise<object>}
   */
  async getCustomer(customerId) {
    const res = await api.getCustomer(customerId);
    return res?.data || res;
  },

  // PT-53A W3.1 (DEF-120): customer writeback from the builder.
  async updateCustomer(customerId, data) {
    const res = await api.updateCustomer(customerId, data);
    return res?.data || res;
  },

  async createCustomer(data) {
    const res = await api.createCustomer(data);
    return res?.data || res;
  },

  /**
   * IDI-305: append an attributed internal note (server stamps author/time).
   * @param {string} quoteId
   * @param {string} content
   * @returns {Promise<Array>} the quote's full notes list after the add
   */
  async addQuoteNote(quoteId, content) {
    const res = await api.addQuoteNote(quoteId, content);
    const body = res?.data ?? res;
    const list = body?.internal_notes ?? body;
    return Array.isArray(list) ? list : [];
  },

  /**
   * IDI-552: replace the text of the caller's own note (server: author only,
   * stamps updated_at). Returns the quote's full notes list after the edit.
   */
  async editQuoteNote(quoteId, noteId, content) {
    const res = await api.editQuoteNote(quoteId, noteId, content);
    const body = res?.data ?? res;
    const list = body?.internal_notes ?? body;
    return Array.isArray(list) ? list : [];
  },

  /** IDI-552: delete the caller's own note. Returns the list after the delete. */
  async deleteQuoteNote(quoteId, noteId) {
    const res = await api.deleteQuoteNote(quoteId, noteId);
    const body = res?.data ?? res;
    const list = body?.internal_notes ?? body;
    return Array.isArray(list) ? list : [];
  },
};

export default quotesAPI;
